/**
 * The telegram theme, turned into css variables.
 *
 * The client picks the colours, not the app: a mini app that ignores themeParams looks like
 * a foreign web page inside telegram. Every variable has a fallback so the app is readable
 * in a plain browser too, where there is no theme at all.
 */

import { getWebApp, isInsideTelegram, type ThemeParams } from "./sdk";

const FALLBACK_LIGHT: Required<Pick<ThemeParams, keyof typeof VARIABLE_OF>> = {
  bg_color: "#ffffff",
  text_color: "#000000",
  hint_color: "#707579",
  link_color: "#3390ec",
  button_color: "#3390ec",
  button_text_color: "#ffffff",
  secondary_bg_color: "#f4f4f5",
  section_bg_color: "#ffffff",
  section_separator_color: "#e5e5e6",
  subtitle_text_color: "#707579",
  destructive_text_color: "#df3f40",
  accent_text_color: "#3390ec",
};

const FALLBACK_DARK: typeof FALLBACK_LIGHT = {
  bg_color: "#17212b",
  text_color: "#ffffff",
  hint_color: "#708499",
  link_color: "#6ab3f3",
  button_color: "#5288c1",
  button_text_color: "#ffffff",
  secondary_bg_color: "#232e3c",
  section_bg_color: "#17212b",
  section_separator_color: "#101921",
  subtitle_text_color: "#708499",
  destructive_text_color: "#ec3942",
  accent_text_color: "#6ab3f3",
};

const VARIABLE_OF = {
  bg_color: "--tg-bg",
  text_color: "--tg-text",
  hint_color: "--tg-hint",
  link_color: "--tg-link",
  button_color: "--tg-button",
  button_text_color: "--tg-button-text",
  secondary_bg_color: "--tg-secondary-bg",
  section_bg_color: "--tg-section-bg",
  section_separator_color: "--tg-separator",
  subtitle_text_color: "--tg-subtitle",
  destructive_text_color: "--tg-destructive",
  accent_text_color: "--tg-accent",
} as const;

/**
 * Whether telegram is the one deciding the colours.
 *
 * The presence of window.Telegram is not the answer: the script is served from telegram.org
 * and loads in a plain browser too, where it reports colorScheme "light" and an empty theme
 * whatever the client's system is set to. Init data is what actually tells the two apart.
 */
function telegramOwnsTheTheme(): boolean {
  return isInsideTelegram();
}

export function applyTheme(): void {
  const webApp = telegramOwnsTheTheme() ? getWebApp() : undefined;
  const scheme = webApp?.colorScheme ?? preferredScheme();
  const fallback = scheme === "dark" ? FALLBACK_DARK : FALLBACK_LIGHT;
  const params = webApp?.themeParams ?? {};

  const root = document.documentElement;
  root.dataset.scheme = scheme;

  for (const [key, variable] of Object.entries(VARIABLE_OF)) {
    const name = key as keyof typeof VARIABLE_OF;
    root.style.setProperty(variable, params[name] ?? fallback[name]);
  }

  // telegram paints behind the app while it is scrolling, so the page background has to
  // match or a differently coloured band shows up at the edges
  document.body.style.backgroundColor = params.bg_color ?? fallback.bg_color;
}

function preferredScheme(): "light" | "dark" {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Follow the theme for as long as the app lives; telegram can change it while it is open. */
export function watchTheme(): () => void {
  applyTheme();

  const webApp = getWebApp();
  if (webApp && telegramOwnsTheTheme()) {
    webApp.onEvent("themeChanged", applyTheme);
    return () => webApp.offEvent("themeChanged", applyTheme);
  }

  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  media?.addEventListener("change", applyTheme);
  return () => media?.removeEventListener("change", applyTheme);
}
